import React from "react";

// A page that is loaded on first use (React.lazy), and survives a new deploy.
//
// Every route page is its own file. When a new version is deployed while the app is open in a
// tab, the files of the old version are gone from the server (it answers 404), so the first
// visit to a page not opened yet in that tab fails to load and the whole screen became
// "Something went wrong on this page". A reload fetches the new version and the page works.
// That is what a new agent met on his first search after the onboarding video (VT review 3/8).
//
// So when a page file fails to load, reload once: the address is already the page asked for,
// and the reload brings the new version. At most one reload a minute per tab, so a file that
// keeps failing ends on the error screen instead of in a reload loop; and no reload while the
// browser says it is offline, where a reload would only replace the app with the browser's own
// "no connection" page.

const RELOADED_AT_KEY = "vt_page_file_reload_at";
export const RELOAD_QUIET_MS = 60 * 1000;

// webpack: ChunkLoadError "Loading chunk 12 failed" / "Loading CSS chunk 12 failed";
// native import(): "Failed to fetch dynamically imported module" (Chrome), "Importing a module
// script failed" (Safari), "error loading dynamically imported module" (Firefox).
export const isPageFileError = (error) =>
  !!error &&
  (error.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk [\w-]+ failed|dynamically imported module|Importing a module script failed/i.test(
      String(error.message || "")
    ));

export const shouldReloadForPageFile = (error, { lastReloadAt = 0, now = Date.now(), online = true } = {}) =>
  isPageFileError(error) && online !== false && !(Number(lastReloadAt) > 0 && now - Number(lastReloadAt) < RELOAD_QUIET_MS);

const defaults = () => ({
  storage: typeof window !== "undefined" ? window.sessionStorage : null,
  reload: () => window.location.reload(),
  now: () => Date.now(),
  online: () => (typeof navigator !== "undefined" ? navigator.onLine : true),
});

// The loader React.lazy is given. Exported so it can be tested without rendering.
export function loadPage(importer, env = {}) {
  const { storage, reload, now, online } = { ...defaults(), ...env };
  return importer().catch((error) => {
    let lastReloadAt = 0;
    try { lastReloadAt = Number(storage && storage.getItem(RELOADED_AT_KEY)) || 0; } catch (e) { /* storage blocked */ }
    if (!shouldReloadForPageFile(error, { lastReloadAt, now: now(), online: online() })) throw error;
    try { if (storage) storage.setItem(RELOADED_AT_KEY, String(now())); } catch (e) { /* storage blocked */ }
    reload();
    // The page is being replaced: stay on the loading state instead of flashing the error screen.
    return new Promise(() => {});
  });
}

export const lazyPage = (importer) => React.lazy(() => loadPage(importer));
